import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

// V2 Rank Progress Visualizer — Explorer → Builder → Achiever → Momentum.
// Receives rank state from /api/users/me/journey (rank.current, rank.next, percent, xp_to_next).
const TIER_ORDER = ['explorer', 'builder', 'achiever', 'momentum'];
const TIER_META = {
  explorer: { name: 'Explorer', color: '#1CB0F6', emoji: '🧭' },
  builder:  { name: 'Builder',  color: '#7E5BEF', emoji: '🔨' },
  achiever: { name: 'Achiever', color: '#FFC800', emoji: '🏆' },
  momentum: { name: 'Momentum', color: '#FF4B4B', emoji: '🚀' },
};

export default function RankProgress({ rank }) {
  if (!rank?.current) return null;
  const currentId = rank.current.id;
  const currentIndex = TIER_ORDER.indexOf(currentId);
  const percent = Math.max(0, Math.min(100, rank.percent || 0));

  return (
    <div className="mt-4" data-testid="rank-progress">
      <p className="text-[10px] uppercase font-extrabold text-[#AFAFAF] tracking-wider mb-2 pl-1">
        Rank journey
      </p>
      <div className="gm-card p-4">
        {/* Tier dots row */}
        <div className="flex items-center justify-between mb-3">
          {TIER_ORDER.map((id, i) => {
            const meta = TIER_META[id];
            const isPast = i < currentIndex;
            const isCurrent = i === currentIndex;
            return (
              <div key={id} className="flex items-center flex-1 last:flex-none" data-testid={`rank-tier-${id}`}>
                <motion.div
                  initial={{ scale: 0.85 }}
                  animate={{ scale: isCurrent ? 1.08 : 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 14 }}
                  className={`relative w-10 h-10 rounded-2xl flex items-center justify-center text-lg ${
                    isCurrent ? 'ring-2 ring-offset-2' : ''
                  }`}
                  style={{
                    backgroundColor: isPast || isCurrent ? meta.color : '#E2E8F0',
                    color: isPast || isCurrent ? '#fff' : '#94A3B8',
                  }}
                >
                  <span aria-hidden>{meta.emoji}</span>
                  {isCurrent && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white border border-current" />
                  )}
                </motion.div>
                {i < TIER_ORDER.length - 1 && (
                  <div className="flex-1 h-1 mx-1 rounded-full bg-[#E2E8F0] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: i < currentIndex ? '100%' : (i === currentIndex ? `${percent}%` : '0%'),
                        backgroundColor: meta.color,
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Labels under dots */}
        <div className="flex items-center justify-between text-[10px] font-extrabold uppercase text-[#94A3B8] tracking-wide">
          {TIER_ORDER.map((id) => (
            <span
              key={`label-${id}`}
              className={id === currentId ? 'text-[#1F2937]' : ''}
            >
              {TIER_META[id].name}
            </span>
          ))}
        </div>

        {/* Status line */}
        <div className="flex items-center gap-2 mt-3 text-xs font-extrabold text-[#4B4B4B]">
          <span>{rank.current.emoji}</span>
          <span>{rank.current.name}</span>
          {rank.next && (
            <>
              <ChevronRight size={14} strokeWidth={3} className="text-[#94A3B8]" />
              <span className="text-[#7E5BEF]" data-testid="rank-next-label">
                {rank.next.name}
              </span>
              <span className="ml-auto text-[#94A3B8]" data-testid="rank-xp-to-next">
                {rank.xp_to_next} XP to go
              </span>
            </>
          )}
          {!rank.next && (
            <span className="ml-auto text-[#FFC800]" data-testid="rank-maxed">Max rank · keep flying</span>
          )}
        </div>

        {/* Inner XP bar */}
        {rank.next && (
          <div className="mt-2 h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: rank.current.color, minWidth: percent > 0 ? undefined : 6 }}
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(percent, percent === 0 ? 2 : 0)}%` }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              data-testid="rank-xp-bar"
            />
          </div>
        )}
      </div>
    </div>
  );
}
